import { useEffect } from 'react';
import { useCustom, useApiUrl } from '@refinedev/core';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TelegramSessionOption } from '@/types';

interface SessionSelectorProps {
  /** Выбранная сессия (id) либо null. */
  value: number | null;
  onChange: (sessionId: number | null) => void;
  /** Грузить список только когда селектор реально нужен. */
  enabled?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

/** Человекочитаемая подпись сессии (имя/username/телефон + владелец). */
const formatSessionLabel = (s: TelegramSessionOption): string => {
  const name =
    [s.telegramFirstName, s.telegramLastName].filter(Boolean).join(' ') ||
    (s.telegramUsername ? `@${s.telegramUsername}` : s.telegramPhone) ||
    'Telegram аккаунт';
  if (s.isShared && s.ownerName) {
    return `${name} (от @${s.ownerName})`;
  }
  return name;
};

/**
 * Выбор Telegram-сессии (userbot) — свои + расшаренные, только AUTHORIZED.
 * Источник: GET /telegram-client/accounts. По умолчанию — активная сессия
 * админа, иначе первая доступная. Переиспользуется в orders и invitations.
 */
export const SessionSelector = ({
  value,
  onChange,
  enabled = true,
  placeholder = 'Выберите Telegram-сессию',
  disabled = false,
}: SessionSelectorProps) => {
  const apiUrl = useApiUrl();

  const { query: sessionsQuery } = useCustom<TelegramSessionOption[]>({
    url: `${apiUrl}/telegram-client/accounts`,
    method: 'get',
    queryOptions: {
      enabled,
    },
  });

  const sessions: TelegramSessionOption[] = (() => {
    const raw = sessionsQuery.data?.data;
    if (Array.isArray(raw)) {
      return raw.filter((s) => s.status === 'AUTHORIZED');
    }
    return [];
  })();

  // Дефолт — активная сессия, иначе первая доступная.
  useEffect(() => {
    if (!enabled || sessions.length === 0) {
      return;
    }
    const stillValid = sessions.some((s) => s.id === value);
    if (!stillValid) {
      const active = sessions.find((s) => s.isActive);
      onChange(active ? active.id : sessions[0].id);
    }
  }, [enabled, sessions, value, onChange]);

  const isEmpty = enabled && !sessionsQuery.isLoading && sessions.length === 0;

  return (
    <div className="space-y-1">
      <Select
        value={value !== null ? String(value) : undefined}
        onValueChange={(v) => onChange(v ? Number(v) : null)}
        disabled={disabled || isEmpty}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {sessions.map((s) => (
            <SelectItem key={s.id} value={String(s.id)}>
              {formatSessionLabel(s)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isEmpty ? (
        <p className="text-xs text-muted-foreground">
          Нет авторизованных Telegram-сессий. Подключите сессию в разделе
          Telegram-клиент.
        </p>
      ) : null}
    </div>
  );
};
